import { useState, useEffect, useRef, useCallback, type ComponentType } from "react";
import { useNavigate } from "react-router";
import {
  BarChart3,
  Bot,
  CheckSquare,
  CreditCard,
  FilePlus2,
  FileText,
  Handshake,
  Pin,
  Plus,
  Search,
  Settings,
  User,
  Workflow,
} from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { useDebounce } from "@/hooks/use-debounce";
import { formatDate } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface SearchResult {
  id: string;
  type: "contact" | "deal" | "task" | "page" | "workflow" | "navigation" | "action";
  title: string;
  subtitle: string;
  path: string;
  icon?: ComponentType<{ className?: string }>;
}

const TYPE_ICONS: Record<SearchResult["type"], ComponentType<{ className?: string }>> = {
  contact: User,
  deal: Handshake,
  task: CheckSquare,
  page: FileText,
  workflow: Workflow,
  navigation: Pin,
  action: Plus,
};

const QUICK_ACTIONS = [
  { id: "new-contact", label: "New Contact", icon: Plus, path: "/crm/contacts" },
  { id: "new-deal", label: "New Deal", icon: Plus, path: "/crm/deals" },
  { id: "new-task", label: "New Task", icon: Plus, path: "/crm/tasks" },
  { id: "new-page", label: "New Page", icon: FilePlus2, path: "/pages/new" },
  { id: "new-workflow", label: "New Workflow", icon: Workflow, path: "/workflows/automations" },
];

const STATIC_GO_TO_RESULTS = [
  {
    id: "go-settings",
    type: "navigation" as const,
    title: "Settings",
    subtitle: "Open workspace settings",
    path: "/settings",
    icon: Settings,
    keywords: ["settings", "preferences", "config"],
  },
  {
    id: "go-ai",
    type: "action" as const,
    title: "AI Mode",
    subtitle: "Focus the AI bar and start asking",
    path: "__focus_ai_bar__",
    icon: Bot,
    keywords: ["ai", "assistant", "ask ai", "copilot"],
  },
  {
    id: "go-analytics",
    type: "navigation" as const,
    title: "Analytics",
    subtitle: "Open analytics dashboards",
    path: "/analytics",
    icon: BarChart3,
    keywords: ["analytics", "reports", "metrics", "dashboard"],
  },
  {
    id: "go-billing",
    type: "navigation" as const,
    title: "Billing",
    subtitle: "Open billing and plan controls",
    path: "/settings/billing",
    icon: CreditCard,
    keywords: ["billing", "plan", "invoice", "subscription"],
  },
] as const;

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const SEARCH_QUERY_MIN_LENGTH = 2;
const LISTBOX_ID = "command-palette-results";

type PaletteItem = {
  id: string;
  type: SearchResult["type"];
  title: string;
  subtitle: string;
  path: string;
  icon?: ComponentType<{ className?: string }>;
};

function normalizeSearchText(value: string) {
  return value.trim().toLowerCase();
}

function matchesQuery(haystack: string, query: string) {
  return normalizeSearchText(haystack).includes(normalizeSearchText(query));
}

/** Global search (Cmd+K) — federated search across contacts, deals, tasks. */
export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PaletteItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchIdRef = useRef(0);
  const navigate = useNavigate();
  const debouncedQuery = useDebounce(query, 200);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setResults([]);
      setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Search on debounced query
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < SEARCH_QUERY_MIN_LENGTH) {
      setResults([]);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    const controller = new AbortController();
    const searchId = ++searchIdRef.current;

    // Federated search — query mounted contacts, deals, tasks, pages, and workflows in parallel.
    Promise.allSettled([
      apiFetch<{ data: { items: Array<{ id: string; email: string | null; firstName: string | null; lastName: string | null }> } }>(
        `/crm/people?search=${encodeURIComponent(debouncedQuery)}&limit=5`,
        { signal: controller.signal },
      ),
      apiFetch<{ data: { items: Array<{ id: string; title: string; value: number | null; currency: string }> } }>(
        `/crm/deals?search=${encodeURIComponent(debouncedQuery)}&limit=5`,
        { signal: controller.signal },
      ),
      apiFetch<{ data: { items: Array<{ id: string; title: string; status: string; dueAt: string | null }> } }>(
        "/crm/tasks?limit=25",
        { signal: controller.signal },
      ),
      apiFetch<{ data: { items?: Array<{ id: string; title: string; slug: string; status: string }> } | Array<{ id: string; title: string; slug: string; status: string }> }>(
        "/crm/pages?limit=25",
        { signal: controller.signal },
      ),
      apiFetch<{ data: Array<{ id: string; name: string; status: string }> }>(
        "/crm/workflows",
        { signal: controller.signal },
      ),
    ]).then(([contactsResult, dealsResult, tasksResult, pagesResult, workflowsResult]) => {
      if (searchId !== searchIdRef.current) {
        return;
      }

      const searchResults: PaletteItem[] = [];
      const allFailed = [contactsResult, dealsResult, tasksResult, pagesResult, workflowsResult]
        .every((result) => result.status === "rejected");

      if (contactsResult.status === "fulfilled") {
        for (const c of contactsResult.value.data.items) {
          const name = [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || "Unknown";
          searchResults.push({
            id: c.id,
            type: "contact",
            title: name,
            subtitle: c.email ?? "",
            path: `/crm/contacts/${c.id}`,
          });
        }
      }

      if (dealsResult.status === "fulfilled") {
        for (const d of dealsResult.value.data.items) {
          if (matchesQuery(d.title, debouncedQuery)) {
            searchResults.push({
              id: d.id,
              type: "deal",
              title: d.title,
              subtitle: d.value != null ? `${d.currency} ${(d.value / 100).toFixed(0)}` : "",
              path: `/crm/deals/${d.id}`,
            });
          }
        }
      }

      if (tasksResult.status === "fulfilled") {
        for (const task of tasksResult.value.data.items) {
          if (matchesQuery(`${task.title} ${task.status}`, debouncedQuery)) {
            searchResults.push({
              id: task.id,
              type: "task",
              title: task.title,
              subtitle: task.dueAt ? `Due ${formatDate(task.dueAt)}` : task.status,
              path: "/crm/tasks",
            });
          }
        }
      }

      if (pagesResult.status === "fulfilled") {
        const pageItems = Array.isArray(pagesResult.value.data)
          ? pagesResult.value.data
          : (pagesResult.value.data.items ?? []);

        for (const page of pageItems) {
          if (matchesQuery(`${page.title} ${page.slug} ${page.status}`, debouncedQuery)) {
            searchResults.push({
              id: page.id,
              type: "page",
              title: page.title,
              subtitle: `/${page.slug} • ${page.status}`,
              path: `/pages/editor/${page.id}`,
            });
          }
        }
      }

      if (workflowsResult.status === "fulfilled") {
        const workflowItems = Array.isArray(workflowsResult.value.data) ? workflowsResult.value.data : [];
        for (const workflow of workflowItems) {
          if (matchesQuery(`${workflow.name} ${workflow.status}`, debouncedQuery)) {
            searchResults.push({
              id: workflow.id,
              type: "workflow",
              title: workflow.name,
              subtitle: workflow.status,
              path: `/workflows/automations?workflowId=${workflow.id}`,
            });
          }
        }
      }

      const staticResults = STATIC_GO_TO_RESULTS.filter((item) =>
        item.keywords.some((keyword) => matchesQuery(keyword, debouncedQuery))
        || matchesQuery(item.title, debouncedQuery)
        || matchesQuery(item.subtitle, debouncedQuery),
      ).map<PaletteItem>(({ keywords: _keywords, ...item }) => item);

      if (allFailed) {
        setSearchError("Search is temporarily unavailable. Please try again.");
      } else {
        setSearchError(null);
      }

      setResults([...staticResults, ...searchResults]);
      setSelectedIdx(0);
    }).finally(() => {
      if (searchId === searchIdRef.current) {
        setIsSearching(false);
      }
    });

    return () => {
      if (searchId === searchIdRef.current) {
        controller.abort();
      }
    };

  }, [debouncedQuery]);

  const allItems: PaletteItem[] = query.length < SEARCH_QUERY_MIN_LENGTH
    ? QUICK_ACTIONS.map((a) => ({ id: a.id, type: "action", title: a.label, subtitle: "", path: a.path, icon: a.icon }))
    : results;

  const handleSelect = useCallback((item: PaletteItem) => {
    if (item.path === "__focus_ai_bar__") {
      window.dispatchEvent(new CustomEvent("ease:focus-ai-bar"));
      onClose();
      return;
    }
    navigate(item.path);
    onClose();
  }, [navigate, onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((i) => {
        if (allItems.length === 0) {
          return i;
        }
        return (i + 1) % allItems.length;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((i) => {
        if (allItems.length === 0) {
          return i;
        }
        return (i - 1 + allItems.length) % allItems.length;
      });
    } else if (e.key === "Enter" && allItems[selectedIdx]) {
      handleSelect(allItems[selectedIdx]);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />

      {/* Palette */}
      <div className="fixed inset-x-0 top-[20%] z-50 mx-auto w-full max-w-lg">
        <div className="rounded-xl border border-border bg-card shadow-2xl">
          {/* Search input */}
          <div className="flex items-center gap-3 border-b border-border px-4 py-3">
            <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search contacts, deals, tasks, pages, workflows..."
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              role="combobox"
              aria-expanded={allItems.length > 0}
              aria-controls={LISTBOX_ID}
              aria-activedescendant={allItems[selectedIdx] ? `command-palette-item-${allItems[selectedIdx].id}` : undefined}
              aria-autocomplete="list"
            />
            <kbd className="rounded border border-border px-1.5 py-0.5 text-xs text-muted-foreground">esc</kbd>
          </div>

          {/* Results */}
          <div id={LISTBOX_ID} role="listbox" className="max-h-72 overflow-y-auto p-2">
            {isSearching && (
              <p className="px-3 py-4 text-center text-sm text-muted-foreground">Searching...</p>
            )}
            {!isSearching && searchError && (
              <p className="px-3 py-4 text-center text-sm text-destructive">{searchError}</p>
            )}
            {!isSearching && !searchError && allItems.length === 0 && query.length >= 2 && (
              <p className="px-3 py-4 text-center text-sm text-muted-foreground">No results found</p>
            )}
            {allItems.map((item, idx) => (
              <button
                key={item.id}
                id={`command-palette-item-${item.id}`}
                role="option"
                aria-selected={idx === selectedIdx}
                onClick={() => handleSelect(item)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                  idx === selectedIdx ? "bg-accent text-accent-foreground" : "text-foreground hover:bg-accent/50",
                )}
              >
                <span className="text-base">
                  {(() => {
                    const Icon = item.icon ?? TYPE_ICONS[item.type as SearchResult["type"]] ?? Pin;
                    return <Icon className="h-4 w-4" aria-hidden="true" />;
                  })()}
                </span>
                <div className="flex-1">
                  <p className="font-medium">{item.title}</p>
                  {item.subtitle && <p className="text-xs text-muted-foreground">{item.subtitle}</p>}
                </div>
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{item.type}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/** Hook to register global Cmd+K listener. */
export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return { isOpen, open: () => setIsOpen(true), close: () => setIsOpen(false) };
}
